import { useState } from "preact/hooks";
import { PHONEMES } from "../content/phonemes";
import type { ArticulatorFamily, Phoneme } from "../content/types";
import { TWISTERS } from "../content/twisters";
import { cardsForPhoneme, masteryFor, soundMapCompletion } from "../engine/soundmap";
import { startSession } from "../state/session";
import { profile } from "../state/store";
import { Button, Panel } from "../components";
import { ArticulatorDiagram } from "./library/ArticulatorDiagram";
import { PhonemeCell } from "./library/PhonemeCell";

const FAMILY_ORDER: ArticulatorFamily[] = ["lips", "tip", "back", "fricative", "nasal", "swedish", "vowel"];
const FAMILY_LABEL: Record<ArticulatorFamily, string> = {
  lips: "Lips",
  tip: "Tongue Tip",
  back: "Back",
  fricative: "Fricatives",
  nasal: "Nasals",
  swedish: "Swedish",
  vowel: "Vowels",
};

const TWISTER_BY_ID = new Map(TWISTERS.map((t) => [t.id, t]));

export function SoundMap() {
  const p = profile.value;
  const [openPhoneme, setOpenPhoneme] = useState<Phoneme | null>(null);
  const completion = soundMapCompletion(p, 3);

  const grouped = FAMILY_ORDER.map((family) => ({
    family,
    phonemes: PHONEMES.filter((ph) => ph.family === family),
  })).filter((g) => g.phonemes.length > 0);

  return (
    <div class="screen screen-map">
      <p class="display screen-title">Sound Map</p>

      <Panel class="map-completion">
        <p class="label">
          {completion.done} / {completion.total} sounds at Mastery 3+
        </p>
        <Button onClick={() => startSession("weak")}>Train my 3 weakest sounds</Button>
      </Panel>

      {grouped.map((g) => (
        <div class="map-group" key={g.family}>
          <p class="label map-group-title">{FAMILY_LABEL[g.family]}</p>
          <div class="map-grid">
            {g.phonemes.map((ph) => (
              <PhonemeCell key={ph.id} phoneme={ph} mastery={masteryFor(ph.id, p)} onOpen={setOpenPhoneme} />
            ))}
          </div>
        </div>
      ))}

      {openPhoneme && (
        <div class="sheet-backdrop" onClick={() => setOpenPhoneme(null)}>
          <div class="sheet" onClick={(e) => e.stopPropagation()}>
            <div class="phoneme-detail-head">
              <ArticulatorDiagram id={openPhoneme.diagramId} size={80} />
              <div>
                <p class="label">{FAMILY_LABEL[openPhoneme.family]}</p>
                <p class="display phoneme-detail-label">{openPhoneme.label}</p>
              </div>
            </div>
            <p class="phoneme-detail-cue">{openPhoneme.cue}</p>
            <p class="mono phoneme-detail-mastery">Mastery {masteryFor(openPhoneme.id, p)} / 5</p>

            <p class="label" style={{ marginTop: 14 }}>
              Cards that train this sound
            </p>
            <ul class="phoneme-detail-cards">
              {cardsForPhoneme(openPhoneme.id)
                .slice(0, 6)
                .map((id) => {
                  const t = TWISTER_BY_ID.get(id);
                  return t ? <li key={id}>{t.text}</li> : null;
                })}
            </ul>

            <Button
              size="lg"
              onClick={() => {
                startSession("weak");
                setOpenPhoneme(null);
              }}
            >
              Train this sound
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
